/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Account Info Post Delivery Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Dmitry Pavlov
 * @author Evgeniy Naumov
 * @version 1.31.0
 * @since 1.11.0
 */

import type { FC } from 'react';

import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { FormBlockStyled } from '@components/common/FormStyled/FormStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import type { IAddEdmConnectionForm } from '@modules/edmDelivery/types';
import { openEdmInquiry } from '@modules/edmDelivery/util/openEdmInquiryModal';
import FormFieldCheckbox from '@modules/form/components/FormFieldCheckbox';
import FormFieldInput from '@modules/form/components/FormFieldInput';
import FormFieldInputPhone from '@modules/form/components/FormFieldInputPhone';
import FormFieldTextarea from '@modules/form/components/FormFieldTextarea';
import FormAddressOpenIcon from '@modules/formAddress/components/FormAddressOpenIcon';
import { formAddressInitializeAction } from '@modules/formAddress/formAddressActions';
import type { TPartialFormAddressConfiguration } from '@modules/formAddress/types/formAddressActionTypes';
import { EFormAddressType } from '@modules/formAddress/types/formAddressTypes';
import {
  makeSelectEdmOperatorConnectionTranslation,
  makeSelectPageAccountInfoTranslation,
  makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import ServiceUserDenied from '@modules/role/components/ServiceUserDenied';
import { CheckboxServiceUserDeniedStyled } from '@modules/role/components/styled/CheckboxServiceUserDeniedStyled';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectUserIsCoordinator,
} from '@pages/PageAuth/selectors/authSelectors';
import type { IRouteParams } from '@routes/constants/routesConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { AccountInfoDeliveryFields } from '../../pageAccountInfoDeliveryConstant';
import type { IAccountDeliveryAddressInfo } from '../../types/pageAccountInfoGeneralTypes';
import type { IAccountInfoDeliveryData } from '../../types/pageAccountInfoTypes';
import { setPostTooltipTitle } from '../../utils/setTooltipTitle';
import { AccountDeliveryTypeStyled } from '../styled/AccountInfoDeliveryStyled';

interface IProps {
  values: IAccountInfoDeliveryData;
  address: IAccountDeliveryAddressInfo;
  inquiryTopic: string | undefined;
  useCourier: boolean;
}

export const AccountDeliveryPost: FC<IProps> = ({
  address,
  inquiryTopic,
  values,
  useCourier,
}) => {
  const dispatch = useDispatch();

  const { clientId } = useParams<IRouteParams>();

  const { delivery: tDelivery } = useSelector(
    makeSelectPageAccountInfoTranslation(),
  );
  const tEdm = useSelector(makeSelectEdmOperatorConnectionTranslation());
  const tCommon = useSelector(makeSelectTranslationCommon());
  const isCoordinator = useSelector(makeSelectUserIsCoordinator());
  const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());
  const isInquiriesEnabled = useSelector(
    makeSelectGuardComponentEnabled(AccessComponentKeyEnum.inquiries),
  );
  const isCustomRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_DOCS_MANAGE);

  const isCheckboxEnabled = isCoordinator || isCustomRoleCheckAvailable;

  const { setFieldValue } = useFormikContext<IAddEdmConnectionForm>();

  const isPostDisableCheckbox = !isCheckboxEnabled || values.useEdm;
  const isPostFieldsDisable =
    !isCheckboxEnabled || !(values.usePost || values.useCourier);

  const handleEditAddress = () => {
    dispatch(formAddressInitializeAction({ address }));
  };

  const handleSubmitAddress = (address: TPartialFormAddressConfiguration) => {
    setFieldValue(AccountInfoDeliveryFields.address, address);
  };

  const handleOpenEdmInquiry = () => {
    openEdmInquiry(
      isInquiriesEnabled,
      isCheckboxEnabled,
      inquiryTopic,
      dispatch,
      clientId,
      tEdm.connectionStatus.inquiryError,
    );
  };

  return (
    <AccountDeliveryTypeStyled className="delivery-last-type">
      <FormBlockStyled className="delivery__address-field-checkbox">
        <ServiceUserDenied
          renderComponent={(disabled) => (
            <ArrowTooltip
              disableHoverListener={isServiceUser}
              maxWidth="320px"
              title={setPostTooltipTitle(
                isCheckboxEnabled,
                isPostDisableCheckbox,
                values,
                tEdm,
              )}
              titleLink={
                isPostDisableCheckbox && values.useEdm ? tEdm.inquiry : ''
              }
              onTitleLinkClick={handleOpenEdmInquiry}
            >
              <CheckboxServiceUserDeniedStyled
                disabled={isPostDisableCheckbox || disabled}
              >
                {useCourier ? (
                  <FormFieldCheckbox
                    dataName="delivery-use-courier"
                    disabled={isPostDisableCheckbox || disabled}
                    label={tDelivery.usePost}
                    name={AccountInfoDeliveryFields.useCourier}
                  />
                ) : (
                  <FormFieldCheckbox
                    dataName="delivery-use-post"
                    disabled={isPostDisableCheckbox || disabled}
                    label={tDelivery.usePost}
                    name={AccountInfoDeliveryFields.usePost}
                  />
                )}
              </CheckboxServiceUserDeniedStyled>
            </ArrowTooltip>
          )}
        />
      </FormBlockStyled>
      <div className="delivery__address-field">
        <FormFieldInput
          disabled
          required
          dataName="delivery-address-string"
          label={tDelivery.addressString}
          name={`${AccountInfoDeliveryFields.address}.addressString`}
        />
        <FormAddressOpenIcon
          initialize={handleEditAddress}
          isDisabled={isPostFieldsDisable}
          type={EFormAddressType.account}
          onSubmit={handleSubmitAddress}
        />
      </div>
      <FormFieldInput
        required
        dataName="delivery-contact-person"
        disabled={isPostFieldsDisable}
        label={tDelivery.contactPerson}
        name={AccountInfoDeliveryFields.contactPerson}
      />
      <FormFieldInputPhone
        required
        dataName="delivery-contact-phone"
        disabled={isPostFieldsDisable}
        label={tDelivery.phone}
        mask={tCommon.phoneInput.mask}
        name={AccountInfoDeliveryFields.phone}
      />
      <FormFieldTextarea
        dataName="delivery-contact-note"
        disabled={isPostFieldsDisable}
        label={tDelivery.note}
        name={AccountInfoDeliveryFields.note}
        rows={4}
      />
    </AccountDeliveryTypeStyled>
  );
};
